import { Empty, Typography } from 'antd';
import { FC, memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { DoctorById_doctorById_data_groups_attributes } from 'gql/profile/__generated__/DoctorById';
import dayjs from 'dayjs';
import { DATE_FORMAT } from 'constants/dates';
import filePlaceholder from 'assets/img/placeholder.png';
import { ReactComponent as Paper } from 'assets/icons/paper.svg';

import Section from '../Section';
import useDoctorProfileCache from 'hooks/useDoctorProfileCache';
import { StyledOverview } from './styled';
import { getFileUrl } from 'utils/file';

const { Paragraph } = Typography;

type GroupedAttributes = {
  rowIndex: number;
  data: DoctorById_doctorById_data_groups_attributes[];
};

const Overview: FC = () => {
  const [searchParams] = useSearchParams();
  const lang = useMemo(() => `${searchParams.get('lang')}`, [searchParams]);
  const { profile } = useDoctorProfileCache({ lang });
  const { t } = useTranslation();

  const groups = useMemo(
    () =>
      profile?.groups.map(group => ({
        ...group,
        attributes: group?.attributes.reduce<GroupedAttributes[]>(
          (acc, val) => {
            const rowIndex = val.row_index;
            const index = acc.findIndex(item => item.rowIndex === rowIndex);

            if (index > -1) {
              acc[index].data.push(val);

              return [...acc];
            }

            return [
              ...acc,
              {
                rowIndex,
                data: [val]
              }
            ];
          },
          []
        )
      })),
    [profile?.groups]
  );

  const renderValue = (item: DoctorById_doctorById_data_groups_attributes) => {
    if (!item.value) {
      return '-';
    }

    switch (item.type) {
      case 'date':
        return dayjs(item.value).format(DATE_FORMAT);
      case 'file':
        return (
          <a
            href={getFileUrl(item.value, filePlaceholder, null)}
            download={item.value}
            target="_blank"
            rel="noreferrer"
          >
            <Paper />
            {item.value}
          </a>
        );
      case 'editor':
        return <span dangerouslySetInnerHTML={{ __html: item.value }}></span>;
      default:
        return item.value;
    }
  };

  return (
    <div>
      {profile?.about && (
        <Section title={t('profile.about_me')}>
          <Paragraph>
            <span dangerouslySetInnerHTML={{ __html: profile?.about }}></span>
          </Paragraph>
        </Section>
      )}
      <StyledOverview>
        {groups?.map(group => (
          <Section key={`group-${group?.id}`} title={`${group?.name}`}>
            <ul>
              {group?.attributes?.length ? (
                group.attributes.map(item => (
                  <li key={`group-item-${item.rowIndex}`}>
                    {item.data.map(attribute => (
                      <div key={`${item.rowIndex}-${attribute.id}`}>
                        {item.data.length > 1 && <b>{attribute.name}: </b>}
                        {renderValue(attribute)}
                      </div>
                    ))}
                  </li>
                ))
              ) : (
                <li>
                  <Empty />
                </li>
              )}
            </ul>
          </Section>
        ))}
      </StyledOverview>
    </div>
  );
};

export default memo(Overview);
